'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

function useIntersectionObserver(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref?.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(el); } },
      { threshold }
    );
    observer?.observe(el);
    return () => observer?.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}

function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'About', href: '/about' },
    { label: 'Our Wines', href: '/wines' },
    { label: 'Winemaking', href: '/winemaking' },
    { label: 'Team', href: '/team' },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <>
      <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center transition-all duration-500"
        style={{ background: 'rgba(10,7,5,0.97)', backdropFilter: 'blur(20px)', opacity: menuOpen ? 1 : 0, pointerEvents: menuOpen ? 'auto' : 'none' }}>
        <button className="absolute top-6 right-6 p-2 text-[#F5EDD6] hover:text-[#C9A84C] transition-colors" aria-label="Close menu" onClick={() => setMenuOpen(false)}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" /></svg>
        </button>
        <nav className="flex flex-col gap-8 text-center">
          {navLinks?.map((link) => (
            <Link key={link?.href + link?.label} href={link?.href}
              className="font-display text-3xl font-medium italic text-[#F5EDD6] hover:text-[#C9A84C] transition-colors duration-300"
              onClick={() => setMenuOpen(false)}>{link?.label}</Link>
          ))}
        </nav>
        <div className="mt-10 text-[#9A8A72] text-xs font-sans uppercase tracking-[0.3em]">Est. 1850 · Pauillac, France</div>
      </div>

      <nav className="fixed z-50 top-0 inset-x-0 px-6 py-3 flex items-center justify-between transition-all duration-500"
        style={{ background: scrolled ? 'rgba(10,7,5,0.85)' : 'transparent', backdropFilter: scrolled ? 'blur(12px)' : 'none', borderBottom: scrolled ? '1px solid rgba(46,26,16,0.6)' : 'none' }}>
        <Link href="/" className="flex items-center gap-3 group">
          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="19" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.6" />
              <path d="M20 8 L24 16 L32 17 L26 23 L28 31 L20 27 L12 31 L14 23 L8 17 L16 16 Z" fill="none" stroke="#C9A84C" strokeWidth="1" />
              <circle cx="20" cy="20" r="3" fill="#C9A84C" fillOpacity="0.5" />
            </svg>
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-base font-semibold tracking-[0.2em] text-[#F5EDD6] uppercase">Baron Castle</span>
            <span className="text-[9px] font-sans tracking-[0.35em] text-[#C9A84C] uppercase mt-0.5">Château Baron · Est. 1850</span>
          </div>
        </Link>
        <div className="hidden lg:flex items-center gap-7">
          {navLinks?.map((link) => (
            <Link key={link?.href + link?.label} href={link?.href}
              className="text-sm font-medium text-[#9A8A72] hover:text-[#F5EDD6] transition-colors duration-300 relative group">
              {link?.label}
              <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-[#C9A84C] group-hover:w-full transition-all duration-300" />
            </Link>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Link href="/contact" className="hidden md:inline-flex items-center gap-2 px-5 py-2 border border-[#C9A84C]/50 text-[#C9A84C] text-xs font-semibold uppercase tracking-widest hover:bg-[#C9A84C] hover:text-[#0A0705] transition-all duration-300">
            Visit Estate
          </Link>
          <button className="lg:hidden p-2 text-[#F5EDD6] hover:text-[#C9A84C] transition-colors" aria-label="Open menu" onClick={() => setMenuOpen(true)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" /></svg>
          </button>
        </div>
      </nav>
    </>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#2E1A10] bg-[#0A0705] pt-16 pb-8">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row justify-between items-start gap-12 mb-12">
          <div className="max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <circle cx="20" cy="20" r="19" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.6" />
                <path d="M20 8 L24 16 L32 17 L26 23 L28 31 L20 27 L12 31 L14 23 L8 17 L16 16 Z" fill="none" stroke="#C9A84C" strokeWidth="1" />
                <circle cx="20" cy="20" r="3" fill="#C9A84C" fillOpacity="0.5" />
              </svg>
              <div className="flex flex-col leading-none">
                <span className="font-display text-lg font-semibold tracking-[0.2em] text-[#F5EDD6] uppercase">Baron Castle</span>
                <span className="text-[9px] font-sans tracking-[0.35em] text-[#C9A84C] uppercase mt-0.5">Château Baron · Est. 1850</span>
              </div>
            </div>
            <p className="text-[#9A8A72] text-sm leading-relaxed italic font-display">A historic Bordeaux château crafting wines of extraordinary depth and elegance since 1850.</p>
            <p className="text-[#9A8A72] text-xs mt-4 font-sans">Route des Châteaux, Saint Lambert<br />33250 Pauillac, Gironde, France</p>
          </div>
          <div className="flex flex-wrap gap-x-16 gap-y-8">
            <div className="flex flex-col gap-3">
              <span className="text-xs font-sans uppercase tracking-widest text-[#C9A84C] mb-1">Estate</span>
              <Link href="/" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Home</Link>
              <Link href="/about" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Our Heritage</Link>
              <Link href="/winemaking" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Winemaking</Link>
            </div>
            <div className="flex flex-col gap-3">
              <span className="text-xs font-sans uppercase tracking-widest text-[#C9A84C] mb-1">Discover</span>
              <Link href="/wines" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Our Wines</Link>
              <Link href="/team" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Team & Investors</Link>
              <Link href="/contact" className="text-sm text-[#9A8A72] hover:text-[#F5EDD6] transition-colors">Visit Us</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-[#2E1A10] pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="text-xs text-[#9A8A72] font-sans">© 2025 圣兰博之围 · Château Baron, Pauillac AOC · All rights reserved</span>
        </div>
      </div>
    </footer>
  );
}

const TIMELINE = [
  { year: '1850', label: 'The Founding', text: 'Henri-Auguste Baron plants the first 12 hectares on the gravel ridges of Saint-Lambert, Pauillac.' },
  { year: '1855', label: 'First Commercial Vintage', text: 'The inaugural 圣兰博之围 wine is produced — the same year as the historic Bordeaux Classification.' },
  { year: '1878', label: 'Cellar Expansion', text: 'Construction of the grand stone cellar, still in use today, capable of aging 600 barriques simultaneously.' },
  { year: '1920', label: 'Post-War Restoration', text: 'Third-generation Charles Baron rebuilds the estate following wartime disruption, replanting 15 hectares.' },
  { year: '1961', label: 'The Legendary Vintage', text: 'The 1961 圣兰博之围 Grand Vin is widely considered the greatest wine ever produced at the estate.' },
  { year: '1995', label: 'Modern Era', text: 'Introduction of parcel-by-parcel vinification and temperature-controlled fermentation under oenologist supervision.' },
  { year: '2008', label: 'Biodynamic Conversion', text: 'Transition to certified biodynamic viticulture across all 45 hectares begins.' },
  { year: '2025', label: 'Today', text: '圣兰博之围 continues as a family-owned estate, producing some of Pauillac\'s most sought-after wines.' },
];

const TERROIR_FACTS = [
  { label: 'Soil Type', value: 'Deep Günzian Gravel', detail: 'Excellent drainage, forces roots 8–12m deep' },
  { label: 'Appellation', value: 'Pauillac AOC', detail: 'Médoc peninsula, Gironde estuary' },
  { label: 'Altitude', value: '18–25m ASL', detail: 'Classic Médoc gravel ridges' },
  { label: 'Vine Age', value: 'Avg. 42 years', detail: 'Oldest parcels planted 1962' },
  { label: 'Encépagement', value: '78% Cab. Sauv.', detail: '15% Merlot · 7% Cab. Franc' },
  { label: 'Harvest', value: 'Hand-picked', detail: 'With optical sorting table' },
];

export default function AboutPage() {
  const { ref: foundingRef, isVisible: foundingVisible } = useIntersectionObserver(0.1);
  const { ref: timelineRef, isVisible: timelineVisible } = useIntersectionObserver(0.05);
  const { ref: terroirRef, isVisible: terroirVisible } = useIntersectionObserver(0.1);

  return (
    <main className="bg-[#0A0705] text-[#F5EDD6] overflow-x-hidden" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6 lg:px-16 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-[#C9A84C]" />
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A84C]">Our Heritage · Since 1850</span>
        </div>
        <h1 className="font-display font-medium text-[#F5EDD6] leading-none select-none" style={{ fontSize: 'clamp(5rem, 16vw, 16rem)' }}>
          The Baron Legacy
        </h1>
        <p className="font-display italic text-lg md:text-xl text-[#F5EDD6]/70 max-w-2xl leading-relaxed mt-6">
          One hundred and seventy-five years of devotion to the vines of Pauillac, guided by a single conviction: that the greatest wines are grown, not made.
        </p>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-16"><div className="h-px bg-[#2E1A10]" /></div>

      {/* Founding Section */}
      <section className="py-24 px-6 lg:px-16 max-w-[1400px] mx-auto">
        <div ref={foundingRef} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start"
          style={{ opacity: foundingVisible ? 1 : 0, transform: foundingVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}>
          <div>
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A84C] block mb-6">01 — The Founding</span>
            <h2 className="font-display font-medium text-[#F5EDD6] leading-tight mb-8" style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}>
              A Vision Born in 1850
            </h2>
            <p className="text-[#9A8A72] leading-relaxed mb-6">
              Henri-Auguste Baron arrived in Pauillac in the spring of 1850, carrying little more than a fortune earned in Bordeaux trade and an unshakeable belief that the gravel ridges of Saint-Lambert held exceptional promise.
            </p>
            <p className="text-[#9A8A72] leading-relaxed mb-6">
              He purchased 12 hectares from the Marquis de Ségur estate — land that had previously supplied fruit to Château Latour — and immediately began planting Cabernet Sauvignon on the deepest gravel outcroppings. His first commercial vintage, the 1855, was produced the same year as the famous Bordeaux Classification, though 圣兰博之围 would not be included in that seminal ranking.
            </p>
            <blockquote className="font-display italic text-[#F5EDD6]/70 text-lg leading-relaxed border-l-2 border-[#C9A84C]/30 pl-6 mb-10">
              "What the classification missed, time will correct."
              <footer className="mt-2 text-[10px] font-sans uppercase tracking-[0.2em] text-[#C9A84C] not-italic">— Henri-Auguste Baron, 1858</footer>
            </blockquote>
            <div className="grid grid-cols-2 gap-4">
              {[
                { v: 'Founded 1850', l: 'Established' },
                { v: '45ha Under Vine', l: 'Estate Size' },
                { v: '80% Cab. Sauv.', l: 'Primary Variety' },
                { v: 'AOC Pauillac', l: 'Appellation' },
              ]?.map(s => (
                <div key={s?.l} className="border-l-2 border-[#C9A84C]/30 pl-4">
                  <span className="font-display text-base font-medium text-[#C9A84C] block">{s?.v}</span>
                  <span className="text-[10px] font-sans uppercase tracking-[0.15em] text-[#9A8A72]">{s?.l}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="w-full bg-[#1A0F08] flex items-center justify-center" style={{ aspectRatio: '4/5' }}>
              <div className="text-center p-12">
                <svg width="80" height="80" viewBox="0 0 40 40" fill="none" className="mx-auto mb-6 opacity-30">
                  <circle cx="20" cy="20" r="19" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.6" />
                  <path d="M20 8 L24 16 L32 17 L26 23 L28 31 L20 27 L12 31 L14 23 L8 17 L16 16 Z" fill="none" stroke="#C9A84C" strokeWidth="1" />
                  <circle cx="20" cy="20" r="3" fill="#C9A84C" fillOpacity="0.5" />
                </svg>
                <span className="font-display italic text-[#F5EDD6]/20 text-2xl">Est. 1850</span>
              </div>
            </div>
            <div className="absolute bottom-6 left-6 right-6 border border-[#C9A84C]/20 bg-[#0A0705]/90 px-6 py-4">
              <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#C9A84C] block mb-1">Château Baron Castle</span>
              <span className="font-display italic text-[#F5EDD6]/60 text-sm">Saint-Lambert · Pauillac · Gironde</span>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-16"><div className="h-px bg-[#2E1A10]" /></div>

      {/* Timeline */}
      <section className="py-24 px-6 lg:px-16 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 mb-14">
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A84C]">02 — A History in Vintages</span>
          <span className="w-8 h-px bg-[#2E1A10]" />
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#9A8A72]">175 Years of Continuity</span>
        </div>
        <div ref={timelineRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0"
          style={{ opacity: timelineVisible ? 1 : 0, transform: timelineVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}>
          {TIMELINE?.map((m, i) => (
            <div key={m?.year} className="border-l border-[#2E1A10] pl-6 pr-4 pb-10 pt-2"
              style={{ transitionDelay: `${i * 0.08}s` }}>
              <span className="font-display text-4xl font-medium text-[#C9A84C]/30 block mb-2">{m?.year}</span>
              <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#C9A84C] block mb-3">{m?.label}</span>
              <p className="text-[#9A8A72] text-sm leading-relaxed">{m?.text}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-16"><div className="h-px bg-[#2E1A10]" /></div>

      {/* Terroir */}
      <section className="py-24 px-6 lg:px-16 max-w-[1400px] mx-auto">
        <div ref={terroirRef} style={{ opacity: terroirVisible ? 1 : 0, transform: terroirVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}>
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A84C] block mb-6">03 — Terroir</span>
          <h2 className="font-display font-medium text-[#F5EDD6] leading-tight mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}>
            The Soil Speaks First
          </h2>
          <p className="text-[#9A8A72] leading-relaxed max-w-2xl mb-14">
            The terroir of Saint-Lambert is Pauillac's best-kept secret — deep Günzian gravel deposited by ancient glaciers, with perfect natural drainage that concentrates flavour and builds structure in ways no winemaker can manufacture.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#2E1A10]">
            {TERROIR_FACTS?.map((fact, i) => (
              <div key={fact?.label} className="border-r border-b border-[#2E1A10] p-8 last:border-r-0"
                style={{ transitionDelay: `${i * 0.08}s` }}>
                <span className="text-[10px] font-sans uppercase tracking-[0.2em] text-[#C9A84C] block mb-2">{fact?.label}</span>
                <span className="font-display text-xl font-medium text-[#F5EDD6] block mb-1">{fact?.value}</span>
                <span className="text-[#9A8A72] text-xs">{fact?.detail}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 flex items-center gap-4">
            <span className="text-[#9A8A72] text-xs font-sans">45 ha Under Vine · Route des Châteaux, Saint-Lambert, Pauillac</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 lg:px-16 max-w-[1400px] mx-auto text-center">
        <p className="font-display italic text-[#F5EDD6]/60 text-lg mb-8">Discover the wines that carry this history forward.</p>
        <Link href="/wines" className="inline-flex items-center gap-3 px-8 py-3 border border-[#C9A84C]/50 text-[#C9A84C] text-xs font-semibold uppercase tracking-widest hover:bg-[#C9A84C] hover:text-[#0A0705] transition-all duration-300">
          Explore Our Wines
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </Link>
      </section>

      <Footer />
    </main>
  );
}
